'use client';


import { useMemo, useState } from 'react';
import {
  calculateSbirNulls,
  primarySbirNull,
  sbirMagnitudeResponse,
  toolFreqGrid,
  type SbirWall,
} from '@/lib/tools/sbir';
import { buildNamedRoomStates, flatMagnitude } from '@/lib/tools/syntheticAcoustics';
import { resizeRoom, type RoomBounds } from '@/lib/tools/roomLayout';
import type { LengthUnit } from '@/lib/tools/units';
import ToolFreqChart from '@/components/tools/ToolFreqChart';
import { ToolMultiAuralization } from '@/components/tools/ToolAuralization';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import RoomPlan2D from '@/components/tools/RoomPlan2D';
import { CONTENT_NAME } from '@/components/seo/roles';

/** Keep wall handles, sliders, and typed values on the same limits. */
const PLAN_BOUNDS: RoomBounds = {
  length: { min: 3, max: 10 },
  width: { min: 2.5, max: 8 },
};

const WALL_COPY: Record<SbirWall, { title: string; detail: string }> = {
  front: { title: 'Front wall', detail: 'wall the speakers face away from (top of plan)' },
  rear: { title: 'Rear wall', detail: 'wall behind the listening area (bottom of plan)' },
  left: { title: 'Left wall', detail: 'left side of the plan' },
  right: { title: 'Right wall', detail: 'right side of the plan' },
};

interface Props {
  initialLengthM: number;
  initialWidthM: number;
  initialSpeakerXM: number;
  initialSpeakerYM: number;
}

export default function SbirWidget({
  initialLengthM,
  initialWidthM,
  initialSpeakerXM,
  initialSpeakerYM,
}: Props) {
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(initialLengthM);
  const [widthM, setWidthM] = useState(initialWidthM);
  const [speakerXM, setSpeakerXM] = useState(initialSpeakerXM);
  const [speakerYM, setSpeakerYM] = useState(initialSpeakerYM);

  const applyRoom = (nextLength: number, nextWidth: number) => {
    const { room, contents } = resizeRoom(
      { lengthM, widthM },
      { lengthM: nextLength, widthM: nextWidth },
      { spk: { xM: speakerXM, yM: speakerYM } },
      { bounds: PLAN_BOUNDS },
    );
    setLengthM(room.lengthM);
    setWidthM(room.widthM);
    setSpeakerXM(contents.spk.xM);
    setSpeakerYM(contents.spk.yM);
  };

  const nulls = useMemo(
    () => calculateSbirNulls({ lengthM, widthM, speakerXM, speakerYM }),
    [lengthM, widthM, speakerXM, speakerYM],
  );
  const primary = useMemo(() => primarySbirNull(nulls), [nulls]);
  const freqs = useMemo(() => toolFreqGrid(20, 300, 160), []);
  const magRoom = useMemo(() => sbirMagnitudeResponse(freqs, nulls), [freqs, nulls]);
  const magPure = useMemo(() => flatMagnitude(freqs), [freqs]);
  const states = useMemo(
    () =>
      buildNamedRoomStates(freqs, [
        { id: 'pure', mag: magPure, nProducts: 0, rt60FineSec: 0.22, dryBroadband: true },
        { id: 'room', mag: magRoom, nProducts: 0, rt60FineSec: 0.5, dryBroadband: false },
      ]),
    [freqs, magPure, magRoom],
  );

  const result = primary
    ? `Your speaker is ${primary.distanceM.toFixed(2)} m from the ${WALL_COPY[primary.wall].title.toLowerCase()}, creating a ${primary.depthDb.toFixed(0)} dB null at ${primary.nullHz.toFixed(0)} Hz.`
    : 'Move the speaker to see boundary cancellation frequencies.';

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <ToolDimSlider
            label="Room length"
            valueM={lengthM}
            minM={3}
            maxM={10}
            unit={unit}
            onChangeM={(v) => applyRoom(v, widthM)}
            testId="dim-length"
          />
          <ToolDimSlider
            label="Room width"
            valueM={widthM}
            minM={2.5}
            maxM={8}
            unit={unit}
            onChangeM={(v) => applyRoom(lengthM, v)}
            testId="dim-width"
          />
          <ToolDimSlider
            label="Speaker from left wall"
            valueM={speakerXM}
            minM={0.2}
            maxM={Math.max(0.3, widthM - 0.2)}
            unit={unit}
            onChangeM={setSpeakerXM}
            testId="dim-spk-x"
          />
          <ToolDimSlider
            label="Speaker from front wall"
            valueM={speakerYM}
            minM={0.2}
            maxM={Math.max(0.3, lengthM - 0.2)}
            unit={unit}
            onChangeM={setSpeakerYM}
            testId="dim-spk-y"
          />

          <RoomPlan2D
            lengthM={lengthM}
            widthM={widthM}
            unit={unit}
            resizeWalls
            roomBounds={PLAN_BOUNDS}
            onRoomSizeChange={applyRoom}
            markers={[
              { id: 'spk', xM: speakerXM, yM: speakerYM, label: 'S', color: 'var(--rt-accent)', draggable: true },
            ]}
            dims={[
              { id: 'front', x1M: speakerXM, y1M: 0, x2M: speakerXM, y2M: speakerYM, offsetPx: -18 },
              { id: 'left', x1M: 0, y1M: speakerYM, x2M: speakerXM, y2M: speakerYM, offsetPx: 18 },
            ]}
            onMarkerMove={(_id, x, y) => {
              setSpeakerXM(x);
              setSpeakerYM(y);
            }}
            testId="sbir-floorplan"
          />
        </div>

        <div className="space-y-4">
          <ToolFreqChart
            freqs={freqs}
            curves={[
              { mag: magPure, tone: 'reference', label: 'Pure' },
              { mag: magRoom, tone: 'accent', label: 'With SBIR' },
            ]}
            markerHz={primary?.nullHz}
            markerLabel={primary ? `${primary.nullHz.toFixed(0)} Hz` : undefined}
          />
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-caption" data-testid="sbir-nulls">
            {nulls.map((n) => {
              const copy = WALL_COPY[n.wall];
              return (
                <li
                  key={n.wall}
                  className="rounded-lg border border-[var(--app-border)] px-3 py-2 text-[var(--rt-ink-dim)]"
                >
                  <div className="flex justify-between gap-2">
                    <span className="text-[var(--rt-ink)] font-medium">{copy.title}</span>
                    <span className="font-mono text-[var(--rt-accent)]">{n.nullHz.toFixed(0)} Hz</span>
                  </div>
                  <div className="text-caption mt-1 text-[var(--rt-ink-dim)]">{copy.detail}</div>
                  <div className="text-caption mt-0.5">
                    {n.distanceM.toFixed(2)} m to wall · {n.depthDb.toFixed(0)} dB dip
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className={CONTENT_NAME}>Hear the dip</h3>
        <ToolMultiAuralization
          states={states}
          stateButtons={[
            { id: 'pure', label: 'Pure' },
            { id: 'room', label: 'Room' },
          ]}
          defaultState="room"
        />
      </div>

      <ToolLeadIn result={result} />
    </div>
  );
}
